/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
import styled from '@emotion/styled';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Info from '@icon/Info';
import {
  isCompletedSelector,
  isOnlyRecordedClassesPresentSelector
} from '@selectors/courses';
import NotificationBell from '@icon/NotificationBell';
import Button from '@base/Button/Button';
import { Spacings, mediaQuery } from '@styles/index';
import {
  ADD_TO_PLANNER_VIEWED,
  CHECKOUT_STARTED,
  COURSE_SHARED,
  COURSE_UPDATES,
  TEST_SERIES_SHARED,
  TEST_SERIES_UPDATES
} from '@constants/segment';
import {
  programmeTypes,
  evtNameCategoryMap,
  subscriptionTypes
} from '@constants/index';
import { segment } from '@actions/analytics';
import { getIsNonOfflineHybridCourse } from '@api/selectors/course';
import Edit from '@icon/Edit';
import Share from '@icon/Share';
import { getPlatformEducator } from '@selectors/users';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro,
  getIsSubscribedToLite,
  getIsSubscribedToOffline,
  getPlusSubscriptionInfoLatest
} from '@selectors/subscription';
import { getIsLoggedIn, getMeUsername } from '@selectors/auth';
import BounceLoader from '@comp/PlusCourseCard/BounceLoader';
import useSubscriptionExpired from '@hooks/useSubscriptionExpired';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import useWindowSize from '@hooks/useWindowSize';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import getIsSubscribedForContent from '@utils/subscriptionHelpers/getIsSubscribedForContent';
import { resolveShowBuy } from '@utils/subscriptionHelpers/resolveShowBuy';
import useUserSubscriptionType from '@hooks/useUserSubscriptionType';
import getContentEnrollButtonLabel from '@utils/subscriptionHelpers/getContentEnrollButtonLabel';
import pushRoute from '@utils/pushRoute';
import segmentStorage from '@events/segmentStorage';
import useConstants from '@api/hooks/useConstants';
import { getIsGTPGoal } from '@api/selectors/constants';
import AddedToList from '@icon/AddedToList';
import AddToList from '@icon/AddToList';
import StartsOn from '../StartsOn';
import SessionCount from './SessionCount';
import Updates from '../Updates';
import DppCount from './DppCount';
import { isOfflineCourseType } from '../utils';

const SessionsInfo = styled(SessionCount)`
  ${mediaQuery.uptoMobile} {
    margin-left: 0px;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const ShareButton = styled(Button)`
  margin-top: ${Spacings.SPACING_3B};
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  .css-1fo6cl-IconWrapper {
    margin: 0;
  }
  ${mediaQuery.uptoMobile} {
    font-size: 14px;
    padding: ${Spacings.SPACING_4B} 0;
    width: 50%;
    .css-1fo6cl-IconWrapper {
      margin: ${Spacings.SPACING_2B};
    }
  }
`;

const HeaderContentRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_6B};
    flex-flow: row-reverse;
    gap: 0;
    & > button:first-child {
      margin-right: 0;
    }
    & > button:last-child {
      margin: 0 ${Spacings.SPACING_2B} 0 0;
    }
  }
`;

const DisabledLoadingButton = styled(Button)`
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
`;

const EditDetailsButton = styled(Button)`
  margin-left: ${Spacings.SPACING_16B};
`;

const EnrollButton = styled(Button)`
  margin-left: ${Spacings.SPACING_16B};
  margin-top: ${({ isEducatorSide }) =>
    isEducatorSide ? '0px' : Spacings.SPACING_5B};
  width: 210px;
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_2B};
    margin-left: 0px;
    font-size: 14px;
    padding: ${Spacings.SPACING_4B} 0;
    width: 50%;
  }
`;

const Buttons = styled(Button)`
  margin-right: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-right: 0px;
    font-size: 14px;
    &:first-child {
      margin-right: ${Spacings.SPACING_2B};
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2B};
    justify-content: space-between;
  }
`;

const BottomWrapper = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_8B};
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    margin-top: 0px;
    flex-direction: column;
  }
`;

const CourseUpdates = ({
  startsAt,
  endsAt,
  isEnrolled,
  lessonCount,
  quizCount,
  classCount,
  openAboutDrawer,
  isTestSeries,
  isEducatorSide,
  plusCourse,
  me,
  goalSegmentData,
  segmentData,
  eventKeys,
  courseType,
  isNeoLesson,
  hidePlannerAndUpdates,
  hasAccessToCourse,
  availableFor,
  setDiscussionForumDrawer,
  isRecordedGoal,
  toggleEnroll,
  setFreePlanUnlockDrawerState,
  setShowShareOptions,
  showLoadingButton,
  setShowDialog,
  openLiteUpgradeDialog
}) => {
  const router = useRouter();
  const { uid: courseUid, goalUid } = plusCourse || {};
  const {
    uid,
    isPrimaryCentreCourse,
    authors,
    isSpecial,
    isIconic,
    isIconicPro,
    isPartOfBatch,
    isStoreExclusive
  } = plusCourse;
  const hasAvailableForList =
    Array.isArray(availableFor) && availableFor.length > 0;
  const isHybridCourse =
    courseType === programmeTypes.HYBRID_COURSE ||
    courseType === programmeTypes.HYBRID_TESTSERIES;
  const isHybridNonOfflineCourse = getIsNonOfflineHybridCourse(plusCourse);
  const isOnlyRecordedClassesPresent = useSelector(
    isOnlyRecordedClassesPresentSelector(plusCourse.uid)
  );
  const { isModerator } = me;
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isPlatformEducator = useSelector(getPlatformEducator);
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUid));
  const [showUpdates, setShowUpdates] = useState(router.query?.showUpdates);
  const username = useSelector(getMeUsername);
  const isIconicUser = useSelector(getIsSubscribedToIconic(goalUid));
  const isIconicProUser = useSelector(getIsSubscribedToIconicPro(goalUid));
  const isPlusUser = useSelector(getIsSubscribedToGoal(goalUid));
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUid));
  const iconicExpired = useSubscriptionExpired(
    goalUid,
    subscriptionTypes.ICONIC
  );
  const iconicProExpired = useSubscriptionExpired(
    goalUid,
    subscriptionTypes.ICONIC_PRO
  );
  const isOfflineCourse = isOfflineCourseType(courseType);
  const isGTP = useConstants(getIsGTPGoal(goalUid));
  const hasSubscription = useSelector(getPlusSubscriptionInfoLatest(goalUid));
  // eslint-disable-next-line no-nested-ternary
  const purchaseType = hasSubscription
    ? hasSubscription.time_remaining
      ? 'extension'
      : 'renewal'
    : 'new';
  const generatedLink = useOneLinkSmartScriptV2({
    campaign: isTestSeries ? 'tests page' : 'course page'
  });
  const plusExpired = useSubscriptionExpired(goalUid);
  const CTALabel = usePurchaseCTAHandler(goalUid);
  const userSubscriptionType = useUserSubscriptionType(goalUid);
  // Store-exclusive content: hasAccessToCourse reflects real purchase
  // status (confirmed false pre-purchase, true post-purchase), and
  // resolveShowBuy additionally requires buying if the user's tier is
  // listed in available_for even without hasAccessToCourse. A FREE-tier
  // user never gets the Enroll treatment even after buying a single
  // store-exclusive course (TPE-9493) — Enroll requires an actual
  // subscription tier above FREE. Only enforce this once available_for is
  // actually configured for the course; otherwise fall back to the plain
  // tier check so unmigrated store-exclusive courses behave unchanged.
  const isSubscribed =
    isStoreExclusive && hasAvailableForList
      ? userSubscriptionType !== subscriptionTypes.FREE &&
        !resolveShowBuy(hasAccessToCourse, availableFor, userSubscriptionType)
      : getIsSubscribedForContent({
          isIconic,
          isIconicPro,
          isIconicUser,
          isIconicProUser,
          isPlusUser
        });
  const { isMobile } = useWindowSize();
  const isPlatformUnlocked = me.platformUnlockStatus?.is_platform_unlocked;
  const isCompleted = useSelector(
    isCompletedSelector(plusCourse.uid, plusCourse.isCompleted)
  );

  const handleEditDetails = () => {
    if (isTestSeries)
      pushRoute(
        '/educator/courses/[type]/edit/[courseUID]',
        `/educator/courses/test-series/edit/${courseUid}`
      );
    else
      pushRoute(
        '/educator/courses/[type]/edit/[courseUID]',
        `/educator/courses/plus-courses/edit/${courseUid}`
      );
  };

  const getButtonTheme = () => {
    if (isEducatorSide) return 'blue';
    if (isSpecial) return isEnrolled ? 'black' : 'green';
    if (
      (iconicExpired && isIconic) ||
      (iconicProExpired && isIconicPro) ||
      plusExpired
    )
      return 'green';
    if (isSubscribed && isEnrolled) return 'black';
    return 'green';
  };
  const getButtonLabel = () => {
    if (isEducatorSide) return 'Discussion Forum';
    return getContentEnrollButtonLabel({
      isSpecial,
      isEnrolled,
      isSubscribed,
      isIconic,
      isIconicPro,
      iconicExpired,
      iconicProExpired,
      plusExpired,
      isIconicUser,
      isIconicProUser,
      isPlusUser,
      ctaLabel: CTALabel,
      enrolledStateLabel: 'enrolled'
    });
  };

  const handleEnroll = async (e, bottomButton) => {
    e.stopPropagation();
    if (
      !isEducatorSide &&
      isLiteUser &&
      ((isTestSeries && isPartOfBatch) || !isTestSeries)
    ) {
      openLiteUpgradeDialog();
      return;
    }
    if (isMobile && bottomButton) {
      window.open(generatedLink, '_blank');
      return;
    }
    if (isEducatorSide) {
      setDiscussionForumDrawer(true);
      return;
    }
    if (isSubscribed || isSpecial) {
      //  if user is subscribed to goal or course is special
      if (!isPlatformUnlocked && isLoggedIn && !isSubscribed) {
        /* if user is not subscribed and is logged in and
          is platform unlocked false */
        setFreePlanUnlockDrawerState(true);
      } else {
        toggleEnroll(uid, isEnrolled, plusCourse);
        if (isCompleted && !isEnrolled && !isTestSeries && !isRecordedGoal) {
          setShowDialog(true);
          segment.track(
            ADD_TO_PLANNER_VIEWED,
            { ...goalSegmentData, ...segmentData },
            evtNameCategoryMap.WITHOUT_CATEGORY,
            true,
            eventKeys
          );
        }
      }
    } else {
      segment.track(
        CHECKOUT_STARTED,
        {
          ...goalSegmentData,
          purchase_type: purchaseType,
          last_primary_source: segmentStorage.getItem('lps'),
          is_upgrade: false
        },
        evtNameCategoryMap.BOTH
      );
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/subscribe',
        `/goal/${plusCourse.goal.slug}/${plusCourse.goal.uid}/subscribe`,
        {},
        'course'
      );
    }
  };

  const handleShare = () => {
    setShowShareOptions(true);
    const eventName =
      courseType === programmeTypes.TESTSERIES
        ? TEST_SERIES_SHARED
        : COURSE_SHARED;
    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...segmentData,
        course_state: plusCourse.state
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const getButtonVariant = () => {
    if (isGTP) return 'disabled';
    if (isEducatorSide) return 'filled';
    if (isSpecial) return isEnrolled ? 'hollow' : 'filled';
    if ((iconicExpired && isIconic) || plusExpired) return 'filled';
    if (isSubscribed && isEnrolled) return 'hollow';
    return 'filled';
  };

  const getIcon = () => {
    if (isEducatorSide) return null;
    const color = isEnrolled
      ? 'var(--color-base-fill)'
      : 'var(--color-i-white)';
    if (isSpecial)
      return isEnrolled ? (
        <AddedToList size={Spacings.SPACING_6B} color={color} />
      ) : (
        <AddToList color={color} size={Spacings.SPACING_6B} />
      );
    if (isSubscribed && isEnrolled)
      return <AddedToList size={Spacings.SPACING_6B} color={color} />;
    if (isSubscribed || isSpecial)
      return <AddToList color={color} size={Spacings.SPACING_6B} />;
  };

  const renderEnrollButton = () => {
    if (isEducatorSide && isTestSeries) return null;
    const author = authors.map((educator) => educator.username?.toLowerCase());
    if (isEducatorSide && !author.includes(username?.toLowerCase()))
      return null;
    const loadingLabel = <BounceLoader />;
    if (showLoadingButton)
      return (
        <DisabledLoadingButton
          variant="disabled"
          color="black"
          label={loadingLabel}
        />
      );
    return (
      <EnrollButton
        isEducatorSide
        variant={getButtonVariant()}
        label={getButtonLabel()}
        color={getButtonTheme()}
        onClick={handleEnroll}
        icon={getIcon()}
      />
    );
  };

  const openUpdatesDrawer = () => {
    const eventName =
      courseType === programmeTypes.TESTSERIES
        ? TEST_SERIES_UPDATES
        : COURSE_UPDATES;
    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...segmentData
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
    setShowUpdates(true);
  };
  const closeUpdatesDrawer = () => setShowUpdates(false);

  const renderHeaderContentRight = () => (
    <HeaderContentRight>
      {!(isOfflineSubscription && isHybridCourse && isPrimaryCentreCourse) &&
        renderEnrollButton()}
      {isEducatorSide && isModerator && (
        <EditDetailsButton
          variant="hollow"
          label="Edit Details"
          onClick={handleEditDetails}
          icon={<Edit size={Spacings.SPACING_6B} />}
          color="black"
        />
      )}
      {!isPlatformEducator && !(isOfflineSubscription && isOfflineCourse) && (
        <ShareButton
          variant="hollow"
          color="black"
          label={isMobile && 'Share'}
          icon={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <Share
              size={Spacings.SPACING_6B}
              color="var(--color-text-primary)"
            />
          }
          onClick={handleShare}
        />
      )}
    </HeaderContentRight>
  );

  if (isEnrolled && !isTestSeries && !isEducatorSide)
    return (
      <BottomWrapper>
        <ButtonWrapper>
          {!hidePlannerAndUpdates && (
            <>
              <Buttons
                variant="hollow"
                grid={3}
                color="black"
                label="Updates"
                icon={
                  // eslint-disable-next-line react/jsx-wrap-multilines
                  <NotificationBell
                    size={Spacings.SPACING_6B}
                    color="var(--color-text-primary)"
                  />
                }
                onClick={openUpdatesDrawer}
              />
              <Updates
                channelId={plusCourse?.channelDetails?.uid}
                showUpdates={showUpdates}
                onCloseUpdates={closeUpdatesDrawer}
                isTestSeries={isTestSeries}
                segmentData={{
                  ...goalSegmentData,
                  ...segmentData
                }}
              />
            </>
          )}
          <Buttons
            variant="hollow"
            color="black"
            grid={3}
            label="About"
            icon={
              <Info
                size={Spacings.SPACING_6B}
                color="var(--color-text-primary)"
              />
            }
            onClick={openAboutDrawer}
          />
        </ButtonWrapper>
        {renderHeaderContentRight()}
      </BottomWrapper>
    );
  return (
    <BottomWrapper>
      {!isOnlyRecordedClassesPresent && (
        <StartsOn startsAt={startsAt} endsAt={endsAt} />
      )}
      <SessionsInfo
        lessonCount={lessonCount}
        quizCount={quizCount}
        classCount={classCount}
        isTestSeries={isTestSeries}
        isHybridNonOfflineCourse={isHybridNonOfflineCourse}
      />
      {!isTestSeries && !isNeoLesson && !isOnlyRecordedClassesPresent && (
        <DppCount />
      )}
      {renderHeaderContentRight()}
    </BottomWrapper>
  );
};

export default CourseUpdates;
